import React, { useEffect, useState } from "react";
import { BarChart3, MessageSquare, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { KpiCard, Badge, ETYPE_META } from "./engShared";

const SERIES = [["messages", "Messages", "bg-brand-cyan/70"], ["requests", "Requests", "bg-brand-amber/70"],
  ["dedications", "Dedications", "bg-pink-400/70"], ["votes", "Poll Votes", "bg-emerald-400/70"],
  ["entries", "Contest Entries", "bg-violet-400/70"]];

export default function EngAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/engagement/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="p-8 text-sm text-ink-muted">Loading…</div>;

  const maxHour = Math.max(1, ...(data.busiest_hours || []).map((h) => h.count));

  return (
    <div className="space-y-5" data-testid="eng-analytics-page">
      <div>
        <h1 className="font-display text-2xl font-bold">Engagement Analytics</h1>
        <p className="text-sm text-ink-secondary">The last 30 days across every listener touchpoint.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {SERIES.map(([k, label]) => (
          <KpiCard key={k} label={`${label} (30d)`} value={data.totals?.[k] ?? 0} testid={`eng-ana-total-${k}`} />
        ))}
        <KpiCard label="Response Rate" value={`${data.response_rate ?? 0}%`}
          accent={data.response_rate >= 60 ? "text-emerald-300" : "text-brand-amber"} testid="eng-ana-response-rate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="eng-ana-daily">
          <div className="flex items-center gap-2 text-sm font-bold mb-3"><BarChart3 className="h-4 w-4 text-brand-cyan" /> Daily activity</div>
          {SERIES.map(([k, label, color]) => {
            const series = data.daily?.[k] || [];
            const max = Math.max(1, ...series.map((d) => d.count));
            return (
              <div key={k} className="mb-2.5">
                <div className="text-[10px] font-mono uppercase text-ink-muted mb-1">{label}</div>
                <div className="flex items-end gap-[2px] h-8">
                  {series.length ? series.map((d, i) => (
                    <div key={i} title={`${d.date}: ${d.count}`} className={`flex-1 rounded-t ${color} min-w-[3px]`}
                      style={{ height: `${Math.max(8, (d.count / max) * 100)}%` }} />
                  )) : <div className="text-[10px] text-ink-muted">No data yet</div>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="eng-ana-hours">
            <div className="flex items-center gap-2 text-sm font-bold mb-3"><Clock className="h-4 w-4 text-brand-amber" /> Busiest hours (UTC)</div>
            <div className="flex items-end gap-[3px] h-20">
              {Array.from({ length: 24 }, (_, h) => {
                const found = (data.busiest_hours || []).find((x) => Number(x.hour) === h);
                const c = found?.count || 0;
                return <div key={h} title={`${String(h).padStart(2, "0")}:00 — ${c}`}
                  className="flex-1 rounded-t bg-brand-amber/60" style={{ height: `${Math.max(4, (c / maxHour) * 100)}%` }} />;
              })}
            </div>
            <div className="flex justify-between text-[9px] font-mono text-ink-muted mt-1"><span>00</span><span>06</span><span>12</span><span>18</span><span>23</span></div>
          </div>

          <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="eng-ana-types">
            <div className="flex items-center gap-2 text-sm font-bold mb-3"><MessageSquare className="h-4 w-4 text-emerald-300" /> Message types</div>
            {!(data.type_breakdown || []).length && <div className="text-xs text-ink-muted">No messages yet.</div>}
            {(data.type_breakdown || []).map((t) => (
              <div key={t.etype} className="flex items-center justify-between py-1">
                <Badge meta={ETYPE_META[t.etype]} />
                <span className="text-xs font-mono text-ink-muted">{t.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
